import { useShadcnNotification } from '@src/components/ui/notification';

import { RenderOptions } from '@shared/components/RenderOptions';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import ProSpinner from '@shared/newComponents/ProSpinner';
import {
    MobileSignSession,
    MobileSignSessionStatus,
} from '@shared/types/process';
import getUrl from '@shared/util/getUrl';
import {
    attachSignPreparationId,
    buildMobileSignSessionCreateCommand,
    CancelDocumentSignCommand,
    getCancelDocumentSignReason,
    needsDocumentSignPreparation,
    PrepareDocumentSignCommand,
    ServiceSignPreparationResult,
    shouldCancelSignPreparation,
} from '@shared/util/signPreparation';

import type {
    MobileSignUrls,
    SignFlowProps,
    SignFlowRef,
    SignFlowTriggerOptions,
} from './SignFlow';

import { ShadcnButton, ShadcnSheet } from '@shadcn-features/core';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

const ACCOUNT_MOBILE_SIGN_URLS: MobileSignUrls = {
    create: 'flow/Account/MobileSignSessions/Create',
    status: 'flow/Account/MobileSignSessions/Status/{id}',
    cancel: 'flow/Account/MobileSignSessions/Cancel/{id}',
};

const InnerMobileSignFlow = <T,>(
    {
        onBeforeTrigger,
        onBeforeSign,
        onAfterSign,
        onResumeMobileSign,
        onSignPreparationSaved,
        mobileSignServiceId,
        mobileSignUrls = ACCOUNT_MOBILE_SIGN_URLS,
        openUri,
        servicesController = 'flow/Services',
        removeMobileSignSessionId,
        extra,
        ...props
    }: SignFlowProps<T>,
    ref: React.Ref<SignFlowRef<T>>,
) => {
    const { t } = useTranslation();
    const notification = useShadcnNotification();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [session, setSession] = useState<MobileSignSession>();
    const [sessionStatus, setSessionStatus] =
        useState<MobileSignSessionStatus>();
    const additionalDataRef = useRef<T>();
    const requestIdRef = useRef(0);
    const sessionIdRef = useRef<string>();
    const preparationIdRef = useRef<string | null>(null);
    const signedSuccessfullyRef = useRef(false);
    const pollRef = useRef<ReturnType<typeof setInterval>>();
    const pollingSessionRef = useRef<string>();
    const completionCallbacksRef = useRef({
        onAfterSign,
        onResumeMobileSign,
        removeMobileSignSessionId,
    });

    useEffect(() => {
        completionCallbacksRef.current = {
            onAfterSign,
            onResumeMobileSign,
            removeMobileSignSessionId,
        };
    }, [onAfterSign, onResumeMobileSign, removeMobileSignSessionId]);

    const handleOpenUri = useCallback(
        (uri: string) => {
            if (openUri) {
                openUri(uri);
                return;
            }

            window.location.assign(uri);
        },
        [openUri],
    );

    const clearPoll = useCallback(() => {
        if (!pollRef.current) return;
        clearInterval(pollRef.current);
        pollRef.current = undefined;
    }, []);

    const cancelMutation = useQueryMutation<string>(
        mobileSignUrls.cancel,
        'post',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig,
                };
            },
        },
    );

    const prepareDocumentSign = useQueryMutation<
        PrepareDocumentSignCommand,
        ServiceSignPreparationResult
    >(getUrl(servicesController, 'PrepareDocumentSign'), 'post');

    const cancelDocumentSign = useQueryMutation<
        CancelDocumentSignCommand,
        unknown
    >(getUrl(servicesController, 'CancelDocumentSign'), 'post');

    const cancelActivePreparation = useCallback(
        async (kind: 'sheetClosed' | 'clientError') => {
            const preparationId = preparationIdRef.current;
            if (
                !shouldCancelSignPreparation({
                    preparationId,
                    signedSuccessfully: signedSuccessfullyRef.current,
                })
            ) {
                return;
            }

            preparationIdRef.current = null;
            if (!preparationId) return;
            try {
                await cancelDocumentSign.mutateAsync({
                    preparationId,
                    reason: getCancelDocumentSignReason(kind),
                });
            } catch (error) {
                console.error(error, 'cancelDocumentSign');
            }
        },
        [cancelDocumentSign],
    );

    const closeDrawer = useCallback(() => {
        const sessionId = sessionIdRef.current;
        requestIdRef.current += 1;
        clearPoll();
        sessionIdRef.current = undefined;
        setSession(undefined);
        setOpen(false);
        if (sessionId) void cancelMutation.mutateAsync(sessionId);
        void cancelActivePreparation('sheetClosed');
    }, [cancelActivePreparation, cancelMutation, clearPoll]);

    useEffect(() => clearPoll, [clearPoll]);

    const createMutation = useQueryMutation<
        ReturnType<typeof buildMobileSignSessionCreateCommand>,
        MobileSignSession
    >(mobileSignUrls.create, 'post');

    const statusMutation = useQueryMutation<string, MobileSignSessionStatus>(
        mobileSignUrls.status,
        'get',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig,
                };
            },
            hideSuccessNotification: true,
        },
    );

    const completeSign = useCallback(
        async (status: MobileSignSessionStatus, sessionId: string) => {
            clearPoll();
            sessionIdRef.current = undefined;
            setLoading(true);
            try {
                const preparationId = preparationIdRef.current;
                signedSuccessfullyRef.current = true;
                const callbacks = completionCallbacksRef.current;
                if (
                    !additionalDataRef.current &&
                    callbacks.onResumeMobileSign
                ) {
                    await callbacks.onResumeMobileSign(status, sessionId);
                } else {
                    await callbacks.onAfterSign?.(
                        attachSignPreparationId(
                            {
                                signData: status.cms,
                                mobileSignSessionId: sessionId,
                                ...additionalDataRef.current,
                                buttonProps: null,
                            },
                            preparationId,
                        ) as T,
                    );
                }
                preparationIdRef.current = null;
                callbacks.removeMobileSignSessionId?.();
                closeDrawer();
            } catch (error) {
                signedSuccessfullyRef.current = false;
                notification.error({
                    message: t('translation:signError'),
                    description: t('translation:signErrorDescription'),
                });
                console.error(error, 'mobileSignError');
            } finally {
                setLoading(false);
            }
        },
        [clearPoll, closeDrawer, notification, t],
    );

    const pollStatus = useCallback(
        async (sessionId: string) => {
            if (pollingSessionRef.current === sessionId) return;
            pollingSessionRef.current = sessionId;
            try {
                const status = await statusMutation.mutateAsync(sessionId);
                if (status) {
                    setSessionStatus(status);
                }
                if (sessionIdRef.current !== sessionId) return;

                if (status.status === 'Completed' && status.cms) {
                    await completeSign(status, sessionId);
                    return;
                }

                if (
                    status.status === 'Consumed' &&
                    !additionalDataRef.current &&
                    completionCallbacksRef.current.onResumeMobileSign
                ) {
                    await completeSign(status, sessionId);
                    return;
                }

                if (
                    status.status === 'Expired' ||
                    status.status === 'Failed' ||
                    status.status === 'Canceled' ||
                    status.status === 'Consumed'
                ) {
                    if (status.status === 'Canceled') {
                        closeDrawer();
                    } else {
                        clearPoll();
                        sessionIdRef.current = undefined;
                    }
                    notification.error({
                        message: t('translation:signError'),
                        description: status.status,
                    });
                }
            } catch (error) {
                // A failed status request is not a cancellation. Retry on the next tick.
                console.error(error, 'mobileSignStatusError');
            } finally {
                if (pollingSessionRef.current === sessionId) {
                    pollingSessionRef.current = undefined;
                }
            }
        },
        [clearPoll, closeDrawer, completeSign, notification, statusMutation, t],
    );

    const startSign = useCallback(
        async (
            data: T,
            requestId: number,
            options?: SignFlowTriggerOptions,
        ) => {
            const buttonId = (data as { buttonId?: unknown }).buttonId;
            const triggerData = data as {
                needsSignPreparation?: boolean;
                signButtonActionId?: string | null;
                serviceId?: string;
                buttonId?: string;
                signPreparationFields?: PrepareDocumentSignCommand['fields'];
            };
            const intent = options?.mobileSignIntent ?? 'ServiceDocumentSign';
            if (!mobileSignServiceId || typeof buttonId !== 'string') {
                throw new Error('mobile sign data not found');
            }

            setLoading(true);
            let ownPreparationId: string | null = null;
            try {
                await onBeforeTrigger?.();
                await onBeforeSign?.();
                const serviceId = triggerData.serviceId;
                const signButtonActionId = triggerData.signButtonActionId;
                if (
                    serviceId &&
                    signButtonActionId &&
                    needsDocumentSignPreparation({
                        needsSignPreparation: triggerData.needsSignPreparation,
                        signButtonActionId,
                        serviceId,
                        buttonId,
                    })
                ) {
                    const prepared = await prepareDocumentSign.mutateAsync({
                        serviceId,
                        buttonId,
                        buttonActionId: signButtonActionId,
                        fields: triggerData.signPreparationFields ?? {},
                    });
                    if (requestId !== requestIdRef.current) {
                        try {
                            await cancelDocumentSign.mutateAsync({
                                preparationId: prepared.preparationId,
                                reason: getCancelDocumentSignReason(
                                    'sheetClosed',
                                ),
                            });
                        } catch (cancelError) {
                            console.error(
                                cancelError,
                                'cancelDocumentSignStale',
                            );
                        }
                        return;
                    }
                    ownPreparationId = prepared.preparationId;
                    preparationIdRef.current = ownPreparationId;
                    if (
                        triggerData.signPreparationFields &&
                        additionalDataRef.current === data
                    ) {
                        onSignPreparationSaved?.(
                            triggerData.signPreparationFields,
                        );
                    }
                }
                const createdSession = await createMutation.mutateAsync(
                    buildMobileSignSessionCreateCommand({
                        intent,
                        serviceId: mobileSignServiceId,
                        buttonId,
                        signButtonActionId: triggerData.signButtonActionId,
                        isMobile: !!openUri,
                        signPreparationId: preparationIdRef.current,
                        fields: triggerData.signPreparationFields,
                    }),
                );
                if (requestId !== requestIdRef.current) {
                    void cancelMutation.mutateAsync(createdSession.sessionId);
                    if (ownPreparationId) {
                        if (preparationIdRef.current === ownPreparationId) {
                            preparationIdRef.current = null;
                        }
                        try {
                            await cancelDocumentSign.mutateAsync({
                                preparationId: ownPreparationId,
                                reason: getCancelDocumentSignReason(
                                    'sheetClosed',
                                ),
                            });
                        } catch (cancelError) {
                            console.error(
                                cancelError,
                                'cancelDocumentSignStale',
                            );
                        }
                    }
                    return;
                }

                sessionIdRef.current = createdSession.sessionId;
                setSession(createdSession);
                pollRef.current = setInterval(() => {
                    void pollStatus(createdSession.sessionId);
                }, 2500);
                void pollStatus(createdSession.sessionId);
                handleOpenUri(createdSession.deepLink);
            } finally {
                setLoading(false);
            }
        },
        [
            cancelDocumentSign,
            cancelMutation,
            createMutation,
            handleOpenUri,
            mobileSignServiceId,
            onBeforeSign,
            onBeforeTrigger,
            onSignPreparationSaved,
            openUri,
            pollStatus,
            prepareDocumentSign,
        ],
    );

    useImperativeHandle(
        ref,
        () => ({
            async trigger(data, _documentIdArg, options) {
                const requestId = requestIdRef.current + 1;
                requestIdRef.current = requestId;
                additionalDataRef.current = data;
                signedSuccessfullyRef.current = false;
                await cancelActivePreparation('sheetClosed');
                setOpen(true);
                try {
                    await startSign(data, requestId, options);
                } catch (error) {
                    closeDrawer();
                    console.error(error, 'mobileSignStartError');
                }
            },

            async checkingStatusSignBySessionId(sessionId: string) {
                try {
                    if (sessionIdRef.current) return;
                    sessionIdRef.current = sessionId;
                    setLoading(true);
                    setOpen(true);
                    pollRef.current = setInterval(() => {
                        void pollStatus(sessionId);
                    }, 2500);
                    void pollStatus(sessionId);
                } finally {
                    setLoading(false);
                }
            },
        }),
        [cancelActivePreparation, closeDrawer, startSign, pollStatus],
    );
    const actions = (
        <>
            <ShadcnButton
                loading={loading}
                size='sm'
                variant='outline'
                onClick={closeDrawer}
            >
                {t('translation:cancel')}
            </ShadcnButton>
            <ShadcnButton
                disabled={!session}
                loading={loading}
                size='sm'
                type='button'
                variant='brandGreen'
                onClick={() => {
                    if (session) handleOpenUri(session.deepLink);
                }}
            >
                {t('translation:egovMobileSignOpen')}
            </ShadcnButton>
        </>
    );

    return (
        <ShadcnSheet
            {...props}
            open={open}
            title={t('translation:signDocument')}
            width='100vw'
            extra={<div className='mr-2 flex items-center gap-2'>{extra}</div>}
            footer={
                <div className='flex w-full gap-3 sm:w-auto sm:justify-end max-md:[&>*]:flex-1'>
                    {actions}
                </div>
            }
            className='!fixed !inset-0 !h-[100dvh] !max-h-none !w-screen !max-w-none !rounded-none'
            styles={{
                body: {
                    display: 'flex',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: 0,
                },
                footer: { padding: '16px 24px' },
            }}
            onClose={closeDrawer}
        >
            <ProSpinner className='h-full w-full' spinning={loading}>
                <div className='mb-2 flex h-full w-full flex-col items-center justify-center p-6 text-center'>
                    <h4>{session && t('translation:egovMobileSignWaiting')}</h4>
                    <div className='mt-2 flex items-center justify-center gap-2 text-center'>
                        <h4>{t('translation:statusSignInEgovMobile')}:</h4>
                        <RenderOptions
                            value={sessionStatus?.status}
                            options={
                                [
                                    {
                                        label: t('translation:pending'),
                                        value: 'Pending',
                                    },
                                    {
                                        label: t('translation:canceled'),
                                        value: 'Canceled',
                                    },
                                    {
                                        label: t('translation:completed'),
                                        value: 'Completed',
                                    },
                                    {
                                        label: t('translation:expired'),
                                        value: 'Expired',
                                    },
                                    {
                                        label: t('translation:failed'),
                                        value: 'Failed',
                                    },
                                    {
                                        label: t('translation:consumed'),
                                        value: 'Consumed',
                                    },
                                ] as {
                                    value: MobileSignSessionStatus['status'];
                                    label: string;
                                }[]
                            }
                        />
                    </div>
                </div>
            </ProSpinner>
        </ShadcnSheet>
    );
};

export const MobileSignFlow = forwardRef(InnerMobileSignFlow) as <
    T extends object,
>(
    props: SignFlowProps<T> & { ref?: React.Ref<SignFlowRef<T>> },
) => ReturnType<typeof InnerMobileSignFlow>;
